import React, { useMemo, useState } from "react";
import { ViewportPortal } from "../shared/ViewportDialog";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { DesktopKpiStrip } from "../shared/DesktopKpiStrip";
import {
  DeskEmpty,
  DeskPanel,
  DeskPill,
  DeskRowMenu,
  DeskSearch,
  DeskTable,
  DeskTabs,
  DeskToolbar,
} from "../desktop/DeskTable";
import { formatFinanceAccountType, formatOpeningBalance } from "../../helpers/financeAccounts";
import { FinanceAccountsSummary } from "./FinanceAccountsSummary";
import "./financeDesktop.css";

const KIND_TONE = { "Money In": "green", "Money Out": "red", Transfer: "gold" };

function signedClass(value) {
  return value > 0 ? "fin-pos" : value < 0 ? "fin-neg" : "";
}

function formatNet(amount, currency) {
  const value = Number(amount);
  const safe = Number.isFinite(value) ? value : 0;
  try {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: currency || "CAD",
      signDisplay: "exceptZero",
    }).format(safe);
  } catch {
    return `${safe > 0 ? "+" : ""}${safe.toFixed(2)} ${currency || "CAD"}`;
  }
}

/** Finance landing (desktop): cash-position KPI strip, accounts panel, today / expected side rail. */
export function FinanceDashboardDesktop({
  position,
  loading,
  error,
  currentUser,
  onOpenAccounts,
  onOpenActivity,
  onOpenMoneyIn,
  onOpenMoneyOut,
  onOpenTransfer,
  onOpenExpected,
  showOverview = true,
  workspaceNav = null,
}) {
  const currency = position?.currency || "CAD";
  const money = (value) => formatOpeningBalance(value ?? 0, currency);
  const moneyInToday = Number(position?.moneyInToday) || 0;
  const moneyOutToday = Number(position?.moneyOutToday) || 0;
  const net = moneyInToday - moneyOutToday;
  const ready = !loading && !error;
  const expectedValue =
    position?.expectedCash === undefined || position?.expectedCash === null
      ? money(0)
      : money(position.expectedCash);

  const tiles = ready
    ? [
        { key: "cur", value: money(position?.currentCash), label: "Current Cash", tone: "gold" },
        { key: "avail", value: money(position?.availableCash), label: "Available Cash", tone: "green" },
        { key: "res", value: money(position?.reservedCash), label: "Reserved Cash", tone: "blue" },
        { key: "tax", value: money(position?.salesTaxHeld), label: "Sales Tax Held", tone: "muted" },
      ]
    : [];

  if (!showOverview) {
    return (
      <CrewPilotModuleShell
        title="Finance"
        subtitle="Open a finance workspace."
        layout={PAGE_LAYOUT.COMMAND}
        primary
        compact
      >
        {workspaceNav}
      </CrewPilotModuleShell>
    );
  }

  return (
    <CrewPilotModuleShell
      title="Finance"
      subtitle="Where is your money, what moved, and what can you safely spend."
      layout={PAGE_LAYOUT.COMMAND}
      primary
      compact
      headerActions={
        <div className="cdh-actions">
          <button type="button" className="dt-btn" onClick={onOpenTransfer}>
            Transfer
          </button>
          <button type="button" className="dt-btn" onClick={onOpenMoneyOut}>
            Money Out
          </button>
          <button type="button" className="dt-btn dt-btn--primary" onClick={onOpenMoneyIn}>
            Money In
          </button>
        </div>
      }
    >
      {error ? <p className="dt-error">{error}</p> : null}
      {loading ? <div className="fin-state">Loading cash position…</div> : null}
      {tiles.length ? <DesktopKpiStrip ariaLabel="Cash position" tiles={tiles} /> : null}
      {workspaceNav}

      <div className="dp-grid dp-grid--main-side">
        <DeskPanel
          title="Accounts"
          actions={
            <button type="button" className="dt-btn" onClick={onOpenAccounts}>
              Manage Accounts
            </button>
          }
        >
          <FinanceAccountsSummary
            accounts={position?.accounts || []}
            currency={currency}
            loading={loading}
            variant="full"
            reorderable
            embedded
            desk
            currentUser={currentUser}
            title="Accounts"
          />
        </DeskPanel>

        <div className="fin-side">
          <DeskPanel title="Today">
            {ready ? (
              <dl className="dp-kv">
                <dt>Money In</dt>
                <dd className="dt-num fin-pos">{money(moneyInToday)}</dd>
                <dt>Money Out</dt>
                <dd className="dt-num fin-neg">{money(moneyOutToday)}</dd>
                <dt>Net Change</dt>
                <dd className={`dt-num ${signedClass(net)}`}>{formatNet(net, currency)}</dd>
              </dl>
            ) : (
              <p className="fin-note">{loading ? "Loading…" : "Not available."}</p>
            )}
          </DeskPanel>

          <DeskPanel
            title="Expected"
            actions={
              <button type="button" className="fin-link" onClick={onOpenExpected}>
                View invoices
              </button>
            }
          >
            {error ? (
              <p className="fin-error">{error}</p>
            ) : loading ? (
              <p className="fin-note">Loading…</p>
            ) : (
              <>
                <p className="fin-big">{expectedValue}</p>
                <p className="fin-hint">From unpaid sent invoices</p>
              </>
            )}
          </DeskPanel>

          <DeskPanel title="Activity" actions={<button type="button" className="fin-link" onClick={onOpenActivity}>Open</button>}>
            <p className="fin-note">Full history of money in, money out, and transfers.</p>
          </DeskPanel>

          <DeskPanel title="Coming Soon">
            <div className="fin-soon">
              <div>
                <strong>Committed</strong>
                <span className="fin-note">Money already spoken for (upcoming bills and obligations).</span>
              </div>
              <DeskPill tone="muted">Soon</DeskPill>
            </div>
            <div className="fin-soon" style={{ marginTop: 14 }}>
              <div>
                <strong>Upcoming Bills</strong>
                <span className="fin-note">Scheduled outflows and payment reminders.</span>
              </div>
              <DeskPill tone="muted">Soon</DeskPill>
            </div>
          </DeskPanel>
        </div>
      </div>
    </CrewPilotModuleShell>
  );
}

const ACTIVITY_TABS = [
  { value: "all", label: "All" },
  { value: "Money In", label: "Money In" },
  { value: "Money Out", label: "Money Out" },
  { value: "Transfer", label: "Transfers" },
];

/** Activity (desktop): filterable ledger table. */
export function FinanceActivityDesktop({
  rows,
  loading,
  error,
  onBack,
  onOpenMoneyIn,
  onOpenMoneyOut,
  onOpenTransfer,
}) {
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");

  const tabs = ACTIVITY_TABS.map((option) => ({
    ...option,
    count: option.value === "all" ? rows.length : rows.filter((r) => r.kind === option.value).length,
  }));

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (tab !== "all" && row.kind !== tab) return false;
      if (!needle) return true;
      return [row.description, row.accountSummary, row.kind, row.occurredOn]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle));
    });
  }, [rows, tab, query]);

  const columns = [
    { key: "date", label: "Date", width: "130px", render: (r) => <span className="dt-nowrap">{r.occurredOn}</span> },
    {
      key: "kind",
      label: "Type",
      width: "130px",
      render: (r) => <DeskPill tone={KIND_TONE[r.kind] || "muted"}>{r.kind}</DeskPill>,
    },
    { key: "desc", label: "Description", render: (r) => <span className="dt-strong">{r.description}</span> },
    { key: "acct", label: "Account", width: "28%", render: (r) => <span className="dt-muted">{r.accountSummary}</span> },
    {
      key: "amt",
      label: "Amount",
      align: "right",
      width: "150px",
      render: (r) => (
        <span className={`dt-num ${r.kind === "Money Out" ? "fin-neg" : r.kind === "Money In" ? "fin-pos" : "fin-gold"}`}>
          {r.kind === "Money Out"
            ? `−${formatOpeningBalance(r.amount, r.currency)}`
            : formatOpeningBalance(r.amount, r.currency)}
        </span>
      ),
    },
  ];

  return (
    <CrewPilotModuleShell
      title="Activity"
      subtitle="History of money in, money out, and transfers."
      onBack={onBack}
      layout={PAGE_LAYOUT.COMMAND}
      compact
      headerActions={
        <div className="cdh-actions">
          <DeskSearch value={query} onChange={setQuery} placeholder="Search activity…" ariaLabel="Search activity" />
          {typeof onOpenTransfer === "function" ? (
            <button type="button" className="dt-btn" onClick={onOpenTransfer}>Transfer</button>
          ) : null}
          {typeof onOpenMoneyOut === "function" ? (
            <button type="button" className="dt-btn" onClick={onOpenMoneyOut}>Money Out</button>
          ) : null}
          {typeof onOpenMoneyIn === "function" ? (
            <button type="button" className="dt-btn dt-btn--primary" onClick={onOpenMoneyIn}>Money In</button>
          ) : null}
        </div>
      }
    >
      {error ? <p className="dt-error">{error}</p> : null}
      {loading ? (
        <div className="fin-state">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="dt-card">
          <DeskEmpty title="No activity yet">
            Record money in, money out, or a transfer to get started.
          </DeskEmpty>
        </div>
      ) : (
        <>
          <DeskToolbar>
            <DeskTabs options={tabs} value={tab} onChange={setTab} ariaLabel="Filter activity" />
            <span className="dt-toolbar__grow" />
            <span className="dt-summary" aria-live="polite">{filtered.length} of {rows.length}</span>
          </DeskToolbar>
          {filtered.length === 0 ? (
            <div className="dt-card"><DeskEmpty title="No matching activity">Try a different filter or search.</DeskEmpty></div>
          ) : (
            <DeskTable columns={columns} rows={filtered} rowKey={(r) => r.id} caption="Finance activity" />
          )}
        </>
      )}
    </CrewPilotModuleShell>
  );
}

/** Accounts (desktop): management table + KPI strip. Modal is supplied by the screen. */
export function FinanceAccountsDesktop({
  accounts,
  loading,
  error,
  busy,
  showInactive,
  onShowInactive,
  onBack,
  onCreate,
  onEdit,
  onDeactivate,
  onReactivate,
  children,
}) {
  const activeCount = accounts.filter((a) => a.active).length;
  const inactiveCount = accounts.length - activeCount;
  const visible = showInactive ? accounts : accounts.filter((a) => a.active);

  const columns = [
    {
      key: "name",
      label: "Account",
      primary: true,
      render: (a) => (
        <>
          <span className="dt-strong">{a.name}</span>
          {a.institution ? <span className="dt-sub">{a.institution}</span> : null}
        </>
      ),
    },
    { key: "type", label: "Type", width: "18%", render: (a) => <span className="dt-muted">{formatFinanceAccountType(a)}</span> },
    { key: "cur", label: "Currency", width: "100px", render: (a) => <span className="dt-muted">{a.currency}</span> },
    {
      key: "open",
      label: "Opening Balance",
      align: "right",
      width: "170px",
      render: (a) => <span className="dt-num">{formatOpeningBalance(a.opening_balance, a.currency)}</span>,
    },
    {
      key: "status",
      label: "Status",
      width: "120px",
      render: (a) => <DeskPill tone={a.active ? "green" : "muted"}>{a.active ? "Active" : "Inactive"}</DeskPill>,
    },
    {
      key: "menu",
      label: "",
      width: "60px",
      className: "dt-cell--actions",
      render: (a) => (
        <DeskRowMenu
          label={`Actions for ${a.name}`}
          items={[
            { key: "edit", label: "Edit" },
            a.active
              ? { key: "deactivate", label: "Deactivate", danger: true }
              : { key: "reactivate", label: "Reactivate" },
          ]}
          onSelect={(item) => {
            if (busy) return;
            if (item.key === "edit") onEdit(a);
            else if (item.key === "deactivate") onDeactivate(a);
            else onReactivate(a);
          }}
        />
      ),
    },
  ];

  return (
    <CrewPilotModuleShell
      title="Accounts"
      subtitle="Create and manage financial accounts. Inactive accounts are retained for history."
      onBack={onBack}
      layout={PAGE_LAYOUT.COMMAND}
      compact
      headerActions={
        <div className="cdh-actions">
          <button type="button" className="dt-btn dt-btn--primary" onClick={onCreate} disabled={busy}>
            + New Account
          </button>
        </div>
      }
    >
      {!loading ? (
        <DesktopKpiStrip
          ariaLabel="Account summary"
          tiles={[
            { key: "act", value: String(activeCount), label: "Active Accounts", tone: "green" },
            { key: "ina", value: String(inactiveCount), label: "Inactive Accounts", tone: "muted" },
          ]}
        />
      ) : null}
      {error ? <p className="dt-error">{error}</p> : null}
      <DeskToolbar>
        <DeskTabs
          options={[
            { value: "all", label: "All", count: accounts.length },
            { value: "active", label: "Active", count: activeCount },
          ]}
          value={showInactive ? "all" : "active"}
          onChange={(v) => onShowInactive(v === "all")}
          ariaLabel="Account visibility"
        />
      </DeskToolbar>
      {loading ? (
        <div className="fin-state">Loading accounts…</div>
      ) : visible.length === 0 ? (
        <div className="dt-card">
          <DeskEmpty title="No accounts yet">Create your first operating account to begin.</DeskEmpty>
        </div>
      ) : (
        <DeskTable
          columns={columns}
          rows={visible}
          rowKey={(a) => a.id}
          onOpen={onEdit}
          openLabel={(a) => `Edit ${a.name}`}
          rowClassName={(a) => (a.active ? "" : "is-muted")}
          caption="Finance accounts"
        />
      )}
      {children}
    </CrewPilotModuleShell>
  );
}

/** Account create/edit modal (desktop): two-column fields. */
export function FinanceModalDesktop({ title, onClose, onSave, busy, children }) {
  return (
    <ViewportPortal>
    <div className="fin-modal" onClick={onClose}>
      <div className="fin-modal__panel" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <h2 className="fin-modal__title">{title}</h2>
        {children}
        <div className="fin-modal__foot">
          <button type="button" className="dt-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="dt-btn dt-btn--primary" onClick={onSave} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
    </ViewportPortal>
  );
}

/** Focused form page (Money In/Out, Transfer): readable width, two-column field grid. */
export function FinanceFormDesktop({
  title,
  subtitle,
  onBack,
  error,
  loading,
  onSubmit,
  submitLabel,
  submitDisabled,
  busy,
  onCancel,
  hint,
  children,
}) {
  return (
    <CrewPilotModuleShell title={title} subtitle={subtitle} onBack={onBack} layout={PAGE_LAYOUT.FORM} compact>
      {error ? <p className="fin-error">{error}</p> : null}
      {loading ? (
        <div className="fin-state">Loading…</div>
      ) : (
        <DeskPanel>
          <form className="fin-form" onSubmit={onSubmit}>
            <div className="fin-form__grid">{children}</div>
            <div className="fin-form__foot">
              <button type="submit" className="dt-btn dt-btn--primary" disabled={submitDisabled}>
                {busy ? "Saving…" : submitLabel}
              </button>
              <button type="button" className="dt-btn" onClick={onCancel} disabled={busy}>
                Cancel
              </button>
            </div>
            {hint ? <p className="fin-note">{hint}</p> : null}
          </form>
        </DeskPanel>
      )}
    </CrewPilotModuleShell>
  );
}
